/**
 * Channels List View
 * 
 * Main page for displaying and managing channel connections (Airbnb, Booking, Gathern).
 * Now uses module's own hooks and services.
 */

import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useChannelsQuery, useGetMsoolUnits } from '../../hooks';
import { ChannelName, ChannelType } from '../../types';
import { T_KEYS } from '../../constants';
import { ChannelCard, ChannelCardSkeleton, ErrorState, TutorialModal, MergeUnitsBanner } from '../../components';
import {
  PageContainer,
  HeaderSection,
  PageTitle,
  PageDescription,
  FirstConnectionBanner,
  BannerContent,
  BannerTitle,
  BannerDescription,
  BannerButton,
  ChannelsGrid,
} from '../../styles';

const ChannelsListView: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State for tutorial modal
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialChannel, setTutorialChannel] = useState<ChannelName>('airbnb');

  // Fetch channels data from API using module's hook
  const { 
    data: channelsData, 
    isLoading, 
    isError, 
    error, 
    refetch 
  } = useChannelsQuery();

  // Extract channels from response
  const channels = useMemo(() => {
    return channelsData?.channels || [];
  }, [channelsData]);

  // Check if any channel is connected - use isConnected from API directly
  const hasAnyConnection = useMemo(() => {
    return channels.some((channel) => channel.isConnected);
  }, [channels]);

  // Check if Airbnb is connected - other channels require Airbnb to be connected first
  const isAirbnbConnected = useMemo(() => {
    const airbnbChannel = channels.find((channel) => channel.channelType === ChannelType.Airbnb);
    return airbnbChannel?.isConnected ?? false;
  }, [channels]);

  // Fetch Msool units to check if any have non-Airbnb channels mapped
  const { data: msoolUnitsData } = useGetMsoolUnits();

  // Check if there are units but none have Booking/Gathern mapped (same logic as UnitsMappingView)
  const hasNoMappedUnits = useMemo(() => {
    if (!msoolUnitsData?.units || msoolUnitsData.units.length === 0) return true;
    return !msoolUnitsData.units.some(unit =>
      unit.channels?.some(c => c.status === true && c.channelType !== ChannelType.Airbnb)
    );
  }, [msoolUnitsData]);

  // Show merge banner when there are Msool units but none have non-Airbnb channels linked
  const showMergeBanner = useMemo(() => {
    return hasAnyConnection && !isLoading && msoolUnitsData?.units && msoolUnitsData.units.length > 0 && hasNoMappedUnits;
  }, [hasAnyConnection, isLoading, msoolUnitsData, hasNoMappedUnits]);

  // Handle connect button click
  const handleConnect = useCallback((channelName: ChannelName) => {
    if (channelName === 'airbnb' || channelName === 'gathern' || channelName === 'booking') {
      setTutorialChannel(channelName);
      setShowTutorial(true);
    } else {
      navigate(`/channels/${channelName}/connect`);
    }
  }, [navigate]);

  // Handle tutorial completion
  const handleTutorialComplete = useCallback(() => {
    setShowTutorial(false);
    navigate(`/channels/${tutorialChannel}/connect`);
  }, [navigate, tutorialChannel]);

  // Handle manage button click
  const handleManage = useCallback((channelName: ChannelName) => {
    navigate(`/channels/${channelName}/manage`);
  }, [navigate]);

  // Render loading skeletons
  const renderSkeletons = () => (
    <ChannelsGrid>
      {[1, 2, 3].map((index) => (
        <ChannelCardSkeleton key={index} />
      ))}
    </ChannelsGrid>
  );

  // Render error state
  const renderError = () => (
    <ErrorState
      message={error?.message}
      onRetry={() => refetch()}
    />
  );

  // Render channels grid
  const renderChannels = () => (
    <>
      {!hasAnyConnection && !isLoading && (
        <FirstConnectionBanner>
          <BannerContent>
            <BannerTitle>{t(T_KEYS.CONNECT_FIRST_CHANNEL)}</BannerTitle>
            <BannerDescription>{t(T_KEYS.CONNECT_FIRST_CHANNEL_DESC)}</BannerDescription>
          </BannerContent>
          <BannerButton onClick={() => handleConnect('airbnb')}>
            {t(T_KEYS.CONNECT_NOW)}
          </BannerButton>
        </FirstConnectionBanner>
      )}

      {showMergeBanner && <MergeUnitsBanner />}

      <ChannelsGrid>
        {channels.map((channel) => {
          const isAirbnb = channel.channelType === ChannelType.Airbnb;
          const shouldDisable = !isAirbnb && !isAirbnbConnected && !channel.isConnected;
          
          return (
            <ChannelCard
              key={channel.channelType}
              channel={channel}
              onConnect={handleConnect}
              onManage={handleManage}
              disabled={shouldDisable}
              disabledMessage={shouldDisable ? t(T_KEYS.CONNECT_AIRBNB_FIRST) : undefined}
            />
          );
        })}
      </ChannelsGrid>
    </>
  );

  return (
    <PageContainer>
      <HeaderSection>
        <PageTitle>{t(T_KEYS.PAGE_TITLE)}</PageTitle>
        <PageDescription>{t(T_KEYS.PAGE_DESCRIPTION)}</PageDescription>
      </HeaderSection>

      {isLoading && renderSkeletons()}
      {isError && renderError()}
      {!isLoading && !isError && renderChannels()}

      <TutorialModal
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={handleTutorialComplete}
        channelName={tutorialChannel}
      />
    </PageContainer>
  );
};

export default ChannelsListView;
